import React, { createContext, useContext, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

type ReportPdfPrintContextValue = {
  /** True after PrintView has snapshotted or discarded the cover map. */
  isCoverFrozen: boolean;
  /** Client snapshot will be injected; skip Map3d in headless Chrome. */
  skipLiveCoverMap: boolean;
};

const ReportPdfPrintContext = createContext<ReportPdfPrintContextValue>({
  isCoverFrozen: false,
  skipLiveCoverMap: false,
});

/**
 * Lets the cover page unmount Map3d after PDF snapshot so Mapbox cannot
 * recreate a WebGL canvas before `page.pdf()`.
 */
export const ReportPdfPrintProvider: FC<{
  isCoverFrozen: boolean;
  skipLiveCoverMap?: boolean;
  children: ReactNode;
}> = ({ isCoverFrozen, skipLiveCoverMap = false, children }) => {
  const value = useMemo(
    () => ({ isCoverFrozen, skipLiveCoverMap }),
    [isCoverFrozen, skipLiveCoverMap],
  );
  return (
    <ReportPdfPrintContext.Provider value={value}>
      {children}
    </ReportPdfPrintContext.Provider>
  );
};

/** Cover freeze flag from {@link ReportPdfPrintProvider}; false outside PrintView. */
export const useReportPdfPrint = (): ReportPdfPrintContextValue =>
  useContext(ReportPdfPrintContext);
